import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, Sparkle, Camera, Trash, CheckCircle, Warning, Plus, ListChecks } from "@phosphor-icons/react";
import AddressAutocomplete from "@/components/AddressAutocomplete";

const PRIORITY_COLORS = {
  urgent: "bg-red-500 text-white",
  high: "bg-orange-500 text-white",
  normal: "bg-slate-200 text-slate-700",
  low: "bg-slate-100 text-slate-500",
};
const CONFIDENCE_COLORS = {
  high: "bg-emerald-500 text-white",
  medium: "bg-amber-500 text-white",
  low: "bg-red-500 text-white",
};

/**
 * Paste an email digest of multiple work orders (and/or a screenshot).
 * AI parses each into a job (incl. per-ticket checklist + unit), admin reviews/edits each card,
 * unchecks any junk rows, then creates them all in one shot.
 */
export default function BulkImportModal({ onClose, onCreated, workers = [] }) {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]); // array of File (up to 6)
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null); // null = pre-parse, [] = parsed
  const [defaultAssignee, setDefaultAssignee] = useState(""); // "" = leave in Pool
  const fileRef = useRef(null);

  const runParse = async () => {
    if (!text.trim() && files.length === 0) {
      toast.error("Paste the email digest or attach at least one screenshot first.");
      return;
    }
    setBusy(true);
    const t = toast.loading("Reading the digest…");
    try {
      const fd = new FormData();
      if (text.trim()) fd.append("text", text.trim());
      files.forEach(f => fd.append("images", f));
      const r = await api.post("/jobs/parse-work-orders-batch", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const jobs = (r.data?.jobs || []).map((j, i) => ({
        ...j,
        unit: j.unit || "",
        checklist: Array.isArray(j.checklist) ? j.checklist : [],
        _include: true,
        _key: `wo-${i}-${Date.now()}`,
      }));
      setRows(jobs);
      toast.success(`Found ${jobs.length} work order${jobs.length === 1 ? "" : "s"} — review each below.`, { id: t });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "AI couldn't read that digest.", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const upd = (idx, key, val) =>
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, [key]: val } : r));
  const updChecklistItem = (idx, ci, val) =>
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, checklist: r.checklist.map((c, k) => k === ci ? val : c) } : r));
  const addChecklistItem = (idx) =>
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, checklist: [...r.checklist, ""] } : r));
  const removeChecklistItem = (idx, ci) =>
    setRows(rs => rs.map((r, i) => i === idx ? { ...r, checklist: r.checklist.filter((_, k) => k !== ci) } : r));
  const removeRow = (idx) => setRows(rs => rs.filter((_, i) => i !== idx));
  const toggleInclude = (idx) => upd(idx, "_include", !rows[idx]._include);

  const included = rows ? rows.filter(r => r._include) : [];

  const runCreate = async () => {
    const jobs = included
      .filter(r => (r.title || "").trim() && (r.property_address || "").trim())
      .map(r => ({
        title: r.title.trim(),
        description: r.description || "",
        property_address: r.property_address.trim(),
        unit: (r.unit || "").trim(),
        customer_name: r.customer_name || "",
        customer_phone: r.customer_phone || "",
        priority: r.priority || "normal",
        entry_code: r.entry_code || "",
        lockbox_code: r.lockbox_code || "",
        entry_notes: r.entry_notes || "",
        work_order_number: r.work_order_number || "",
        assigned_to: defaultAssignee || undefined,
        checklist: (r.checklist || []).map(c => (c || "").trim()).filter(Boolean),
      }));
    if (!jobs.length) {
      toast.error("Every job needs at least a title and an address.");
      return;
    }
    setBusy(true);
    const t = toast.loading(`Creating ${jobs.length} job${jobs.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/jobs/bulk-create", { jobs });
      toast.success(`Created ${r.data.created} job${r.data.created === 1 ? "" : "s"}`, { id: t });
      onCreated?.(r.data.ids || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Bulk create failed", { id: t });
    } finally {
      setBusy(false);
    }
  };

  const missingCount = rows ? included.filter(r => !(r.title || "").trim() || !(r.property_address || "").trim()).length : 0;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[92vh] overflow-y-auto bg-white border-2 border-[#0f172a] shadow-[8px_8px_0px_#0f172a]" onClick={e => e.stopPropagation()} data-testid="bulk-import-modal">
        <div className="sticky top-0 z-10 bg-white border-b-2 border-[#0f172a] p-5 flex items-center justify-between">
          <div>
            <h3 className="font-display font-black text-2xl inline-flex items-center gap-2">
              <Sparkle weight="fill" size={22} className="text-[#00C4CC]"/> AI Bulk Import
            </h3>
            <p className="text-xs text-slate-600 font-medium mt-0.5">
              Paste an email digest of work orders. AI splits them, extracts each unit + per-ticket checklist. You review, uncheck junk, then create them all in one click.
            </p>
          </div>
          <button onClick={onClose} className="p-1"><X weight="bold" size={22}/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="rugged-card p-3 bg-emerald-50 border-emerald-500 flex items-start gap-3" data-testid="bulk-import-free-alternative-banner">
            <span className="text-2xl leading-none">💡</span>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black uppercase tracking-widest text-emerald-800">Free CSV importer available</div>
              <div className="text-sm text-slate-700 mt-0.5">
                Importing an <b>Appfolio CSV export</b>? Use the <b>free CSV importer</b> — no AI credits used, dedupes by WO#, auto-creates properties.
              </div>
            </div>
            <a href="/admin/imports/appfolio"
              data-testid="bulk-import-goto-free-importer"
              className="shrink-0 self-center h-9 px-3 bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase tracking-widest text-[10px] inline-flex items-center gap-1">
              Use free importer →
            </a>
          </div>
          {rows === null ? (
            <>
              <textarea
                rows={10}
                value={text}
                onChange={(e) => setText(e.target.value)}
                data-testid="bulk-import-text"
                placeholder={"Paste the whole email or SMS digest here.\n\nExample:\n---\nWO-1234 · 4823 Maplewood Dr · Apt 3B · Maria Gonzalez 502-555-0193\nLeaky kitchen faucet — dripping under sink, tenant put a bucket. Also bathroom sink slow to drain. Please respond today.\n\nWO-1235 · 219 High St · John Smith 502-555-0111\nHVAC not cooling — thermostat reads 82F. URGENT.\n---"}
                className="w-full px-3 py-2 border-2 border-[#0f172a] text-sm font-medium bg-white focus:outline-none focus:ring-4 focus:ring-[#00C4CC]/30"
              />
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => {
                    const picks = Array.from(e.target.files || []);
                    setFiles(prev => {
                      const seen = new Set(prev.map(f => f.name + f.size));
                      const merged = [...prev];
                      for (const p of picks) {
                        const k = p.name + p.size;
                        if (!seen.has(k) && merged.length < 6) { seen.add(k); merged.push(p); }
                      }
                      return merged;
                    });
                    if (fileRef.current) fileRef.current.value = "";
                  }}
                  data-testid="bulk-import-file"
                  className="hidden"
                />
                <button type="button" onClick={() => fileRef.current?.click()}
                  data-testid="bulk-import-attach-btn"
                  className="h-11 px-3 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider bg-white hover:bg-[#0f172a] hover:text-white transition-colors inline-flex items-center gap-2">
                  <Camera weight="bold" size={14}/>
                  {files.length === 0 ? "Attach screenshots" : `Add more (${files.length}/6)`}
                </button>
                <div className="flex-1"/>
                <button type="button" onClick={runParse} disabled={busy}
                  data-testid="bulk-import-parse-btn"
                  className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                  <Sparkle weight="fill" size={16}/> {busy ? "Reading…" : "Parse with AI"}
                </button>
              </div>
              {files.length > 0 && (
                <div className="mt-1 flex flex-wrap gap-2" data-testid="bulk-import-file-list">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 h-8 px-2 bg-white border-2 border-[#0f172a] text-xs font-bold" data-testid={`bulk-import-file-chip-${i}`}>
                      <Camera weight="bold" size={12}/>
                      <span className="max-w-[160px] truncate">{f.name}</span>
                      <button type="button" onClick={() => setFiles(fs => fs.filter((_, j) => j !== i))}
                        aria-label="Remove screenshot"
                        className="ml-1 text-slate-500 hover:text-red-500">
                        <X weight="bold" size={12}/>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <div className="text-xs font-black uppercase tracking-widest text-slate-500">
                  {included.length} of {rows.length} will be created
                </div>
                <button onClick={() => { setRows(null); setText(""); setFiles([]); }}
                  className="text-xs font-black uppercase tracking-widest text-slate-500 hover:text-[#0f172a] underline underline-offset-2">
                  ← Start over
                </button>
              </div>

              {/* Bulk-assign this batch to one worker (optional; leave blank to keep in Pool) */}
              <div className="rugged-card p-3 bg-[#00C4CC]/10 border-[#00C4CC] flex items-center gap-3 flex-wrap">
                <label className="text-[10px] font-black uppercase tracking-widest text-[#0f172a]">
                  Assign whole batch to
                </label>
                <select
                  value={defaultAssignee}
                  onChange={(e) => setDefaultAssignee(e.target.value)}
                  data-testid="bulk-import-default-assignee"
                  className="h-10 px-2 border-2 border-[#0f172a] text-sm font-bold bg-white flex-1 min-w-[180px]"
                >
                  <option value="">— Leave in Pool —</option>
                  {workers.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name}{w.role === "admin" ? " (office)" : ""}
                    </option>
                  ))}
                </select>
                {defaultAssignee && (
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#0f172a]">
                    Applies to all {included.length} job{included.length === 1 ? "" : "s"}
                  </span>
                )}
              </div>

              {rows.length === 0 && (
                <div className="rugged-card p-4 bg-amber-50 border-amber-500 text-sm font-medium">
                  <Warning weight="fill" size={18} className="inline mr-2 text-amber-600"/>
                  AI didn't find any work orders in that input. Try adding more detail or a screenshot.
                </div>
              )}

              {rows.map((r, i) => {
                const bad = !(r.title || "").trim() || !(r.property_address || "").trim();
                return (
                  <div key={r._key}
                    data-testid={`bulk-import-row-${i}`}
                    className={`rugged-card p-4 ${r._include ? "bg-white" : "bg-slate-100 opacity-60"} ${bad && r._include ? "border-red-500" : ""}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={r._include}
                        onChange={() => toggleInclude(i)}
                        data-testid={`bulk-import-include-${i}`}
                        aria-label="Include this job"
                        className="w-5 h-5 accent-[#00C4CC] cursor-pointer flex-shrink-0"
                      />
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <span className={`h-6 px-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center rounded-sm ${PRIORITY_COLORS[r.priority] || PRIORITY_COLORS.normal}`}>
                          {r.priority}
                        </span>
                        {r.work_order_number && (
                          <span className="h-6 px-2 bg-[#0f172a] text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center rounded-sm font-mono">
                            WO# {r.work_order_number}
                          </span>
                        )}
                        {r.unit && (
                          <span className="h-6 px-2 bg-fuchsia-600 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center rounded-sm">
                            Unit {r.unit}
                          </span>
                        )}
                        {r.confidence && (
                          <span className={`h-6 px-2 font-black uppercase text-[10px] tracking-widest inline-flex items-center rounded-sm ${CONFIDENCE_COLORS[r.confidence] || CONFIDENCE_COLORS.medium}`}>
                            AI · {r.confidence}
                          </span>
                        )}
                      </div>
                      <button onClick={() => removeRow(i)}
                        data-testid={`bulk-import-remove-${i}`}
                        className="p-1.5 text-slate-500 hover:text-red-500" aria-label="Remove this row">
                        <Trash weight="bold" size={16}/>
                      </button>
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Title *</label>
                        <input value={r.title} onChange={e => upd(i, "title", e.target.value)}
                          data-testid={`bulk-import-title-${i}`}
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium"/>
                      </div>
                      <div className="sm:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Address *</label>
                        <AddressAutocomplete
                          value={r.property_address}
                          onChange={(v) => upd(i, "property_address", v)}
                          onSelect={({ label }) => upd(i, "property_address", label)}
                          placeholder="Base street — no unit"
                          testId={`bulk-import-address-${i}`}
                        />
                      </div>
                      <div className="sm:col-span-1">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Unit / Apt</label>
                        <input value={r.unit} onChange={e => upd(i, "unit", e.target.value)}
                          data-testid={`bulk-import-unit-${i}`}
                          placeholder="A · 3B · 101"
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Customer</label>
                        <input value={r.customer_name} onChange={e => upd(i, "customer_name", e.target.value)}
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Phone</label>
                        <input value={r.customer_phone} onChange={e => upd(i, "customer_phone", e.target.value)}
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-medium"/>
                      </div>
                      <div className="sm:col-span-2">
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Description</label>
                        <textarea rows={2} value={r.description} onChange={e => upd(i, "description", e.target.value)}
                          className="w-full px-2 py-1.5 border-2 border-[#0f172a] text-sm font-medium resize-none"/>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">Priority</label>
                        <select value={r.priority} onChange={e => upd(i, "priority", e.target.value)}
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-bold uppercase">
                          <option value="low">Low</option>
                          <option value="normal">Normal</option>
                          <option value="high">High</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">WO #</label>
                        <input value={r.work_order_number} onChange={e => upd(i, "work_order_number", e.target.value)}
                          className="w-full h-10 px-2 border-2 border-[#0f172a] text-sm font-mono"/>
                      </div>
                    </div>

                    {/* AI-extracted per-ticket checklist */}
                    <div className="mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1">
                          <ListChecks weight="bold" size={12}/>
                          Checklist for this ticket
                          {r.checklist.length > 0 && <span className="text-slate-400">· {r.checklist.length}</span>}
                        </label>
                        <button type="button" onClick={() => addChecklistItem(i)}
                          data-testid={`bulk-import-add-check-${i}`}
                          className="text-[10px] font-black uppercase tracking-widest text-[#00C4CC] hover:underline inline-flex items-center gap-0.5">
                          <Plus weight="bold" size={10}/> Add step
                        </button>
                      </div>
                      {r.checklist.length === 0 ? (
                        <div className="text-xs text-slate-400 font-medium italic px-2 py-1.5 bg-slate-50 border border-dashed border-slate-300">
                          AI didn't extract distinct sub-issues — add any yourself.
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          {r.checklist.map((c, ci) => (
                            <div key={ci} className="flex items-center gap-1.5">
                              <span className="text-[10px] font-black text-slate-400 w-4 flex-shrink-0">{ci + 1}.</span>
                              <input value={c} onChange={e => updChecklistItem(i, ci, e.target.value)}
                                data-testid={`bulk-import-check-${i}-${ci}`}
                                className="flex-1 h-9 px-2 border-2 border-[#0f172a] text-xs font-medium"/>
                              <button type="button" onClick={() => removeChecklistItem(i, ci)}
                                aria-label="Remove checklist item"
                                className="p-1 text-slate-400 hover:text-red-500 flex-shrink-0">
                                <X weight="bold" size={12}/>
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {(r.entry_code || r.lockbox_code || r.entry_notes) && (
                      <div className="mt-3 text-xs text-slate-600 font-medium bg-amber-50 border-l-4 border-amber-500 px-3 py-2">
                        {r.entry_code && <div>Entry code: <span className="font-mono font-black">{r.entry_code}</span></div>}
                        {r.lockbox_code && <div>Lockbox: <span className="font-mono font-black">{r.lockbox_code}</span></div>}
                        {r.entry_notes && <div>{r.entry_notes}</div>}
                      </div>
                    )}
                    {bad && r._include && (
                      <div className="mt-2 text-xs font-bold text-red-600">
                        <Warning weight="fill" size={12} className="inline mr-1"/> Title and address are required to create this job.
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}
        </div>

        {rows !== null && rows.length > 0 && (
          <div className="sticky bottom-0 bg-white border-t-2 border-[#0f172a] p-4 flex items-center justify-between gap-3">
            <div className="text-xs font-medium text-slate-500">
              {missingCount > 0
                ? <span className="text-red-600 font-black uppercase tracking-widest">{missingCount} row{missingCount === 1 ? "" : "s"} missing title / address</span>
                : <span><CheckCircle weight="fill" size={14} className="inline mr-1 text-emerald-500"/>Ready to create</span>}
            </div>
            <div className="flex gap-2">
              <button onClick={onClose} className="h-11 px-4 border-2 border-[#0f172a] font-black uppercase text-xs tracking-wider">Cancel</button>
              <button onClick={runCreate} disabled={busy || included.length === 0}
                data-testid="bulk-import-create-btn"
                className="tactile-btn h-11 px-5 bg-[#00C4CC] text-white font-black uppercase text-sm tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                <CheckCircle weight="fill" size={16}/> {busy ? "Creating…" : `Create ${included.length} Job${included.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
